import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { apiFetch, useToast, useAuth } from '../../App';
import { ga } from '../../utils/ga4';
import { Plus, Edit3, Trash2, Save, X, Eye, Search, Loader2, Globe, FileText, Archive, Image as ImageIcon, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABELS = { draft: 'Draft', published: 'Published', archived: 'Archived' };
const STATUS_COLORS = {
  draft: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  archived: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};
const CATEGORIES = ['Grooming Tips', 'Pet Care', 'Health & Wellness', 'Franchise & Business', 'Reviews', 'Lifestyle'];

const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote'],
      ['link', 'image'],
      [{ align: [] }],
      ['clean'],
    ],
  },
  clipboard: { matchVisual: false },
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'blockquote', 'link', 'image', 'align',
];

function emptyForm() {
  return {
    id: null,
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    coverImageUrl: '',
    category: 'Pet Care',
    tags: '',
    status: 'draft',
  };
}

export default function DenBlog() {
  const addToast = useToast();
  const { user } = useAuth();
  const canManage = user && ['MASTER_ADMIN', 'ORG_OWNER', 'EDITOR'].includes(user.role);

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | id string
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const quillRef = useRef(null);

  const loadBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const path = statusFilter ? `/api/admin/blog?status=${statusFilter}` : '/api/admin/blog';
      const data = await apiFetch(path);
      setBlogs(data);
    } catch (e) {
      addToast(e.message, 'error');
    }
    setLoading(false);
  }, [statusFilter, addToast]);

  useEffect(() => { loadBlogs(); }, [loadBlogs]);

  const filtered = useMemo(() => {
    if (!search) return blogs;
    const s = search.toLowerCase();
    return blogs.filter(b => (b.title || '').toLowerCase().includes(s) || (b.category || '').toLowerCase().includes(s) || (b.tags || []).some(t => t.toLowerCase().includes(s)));
  }, [blogs, search]);

  const startNew = () => {
    setForm(emptyForm());
    setEditing('new');
  };

  const startEdit = async (id) => {
    try {
      const blog = await apiFetch(`/api/admin/blog/${id}`);
      setForm({
        id: blog.id,
        title: blog.title || '',
        slug: blog.slug || '',
        excerpt: blog.excerpt || '',
        content: blog.content || '',
        coverImageUrl: blog.coverImageUrl || '',
        category: blog.category || 'Pet Care',
        tags: (blog.tags || []).join(', '),
        status: blog.status || 'draft',
      });
      setEditing(id);
    } catch (e) { addToast(e.message, 'error'); }
  };

  const cancelEdit = () => { setEditing(null); setForm(emptyForm()); };

  // --- Image upload (used both for cover image and inline editor images) ---
  const uploadImage = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('petfolk_token');
    const res = await fetch(`${API}/api/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    const data = await res.json();
    return data.url;
  }, []);

  // Custom Quill image handler — uploads to /api/upload, inserts URL
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        addToast('Uploading image…', 'info');
        const url = await uploadImage(file);
        const editor = quillRef.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', url, 'user');
          editor.setSelection(range.index + 1);
        }
        addToast('Image inserted');
      } catch (e) { addToast(e.message, 'error'); }
    };
  }, [uploadImage, addToast]);

  const modules = useMemo(() => ({
    ...QUILL_MODULES,
    toolbar: { ...QUILL_MODULES.toolbar, handlers: { image: imageHandler } },
  }), [imageHandler]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setForm(f => ({ ...f, coverImageUrl: url }));
      addToast('Cover image uploaded');
    } catch (err) { addToast(err.message, 'error'); }
  };

  const handleSave = async (statusOverride) => {
    if (!form.title.trim() || !form.content.trim() || form.content === '<p><br></p>') {
      addToast('Title and content are required', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content,
        excerpt: form.excerpt.trim(),
        coverImageUrl: form.coverImageUrl || null,
        category: form.category,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        status: statusOverride || form.status,
      };
      if (form.slug && form.slug.trim()) payload.slug = form.slug.trim();
      let result;
      const fromStatus = form.id ? (blogs.find(b => b.id === form.id)?.status || 'draft') : 'new';
      if (form.id) {
        result = await apiFetch(`/api/admin/blog/${form.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        result = await apiFetch('/api/admin/blog', { method: 'POST', body: JSON.stringify(payload) });
      }
      // GA4: track publish/draft transitions
      if (result?.slug && (fromStatus !== result.status || fromStatus === 'new')) {
        ga.blogPublished(result.slug, fromStatus, result.status);
      }
      addToast(`Blog ${form.id ? 'updated' : 'created'} (${STATUS_LABELS[result.status]})`);
      setEditing(null);
      setForm(emptyForm());
      loadBlogs();
    } catch (e) { addToast(e.message, 'error'); }
    setSaving(false);
  };

  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/admin/blog/${blog.id}`, { method: 'DELETE' });
      addToast('Blog deleted');
      loadBlogs();
    } catch (e) { addToast(e.message, 'error'); }
  };

  const togglePublish = async (blog) => {
    const newStatus = blog.status === 'published' ? 'draft' : 'published';
    try {
      await apiFetch(`/api/admin/blog/${blog.id}`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
      ga.blogPublished(blog.slug, blog.status, newStatus);
      addToast(newStatus === 'published' ? 'Blog published' : 'Blog moved to draft');
      loadBlogs();
    } catch (e) { addToast(e.message, 'error'); }
  };

  if (!canManage) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center" data-testid="blog-no-access">
        <h1 className="font-outfit text-2xl font-bold mb-2">Blog Access Required</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Only Master Admin and Editor roles can manage blog content.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="den-blog-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-outfit text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Blog</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Write, edit & publish stories. Public articles appear at /blog.</p>
        </div>
        {!editing && (
          <button data-testid="blog-new-btn" onClick={startNew} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} /> New Post
          </button>
        )}
      </div>

      {/* Editor */}
      {editing && (
        <div className="rounded-2xl border p-5 mb-8" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }} data-testid="blog-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit text-xl font-bold">{form.id ? 'Edit Post' : 'New Post'}</h2>
            <button onClick={cancelEdit} className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" data-testid="blog-cancel-btn">
              <X size={18} />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Title *</label>
              <input
                data-testid="blog-title-input"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="A compelling headline…"
                className="w-full px-4 py-3 rounded-xl border bg-transparent font-outfit text-lg font-semibold focus:outline-none focus:border-petfolk-orange"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Slug (optional)</label>
              <input
                data-testid="blog-slug-input"
                value={form.slug}
                onChange={e => setForm({ ...form, slug: e.target.value })}
                placeholder="auto-generated from title"
                className="w-full px-3 py-2 rounded-xl border bg-transparent text-sm focus:outline-none focus:border-petfolk-orange"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Category</label>
              <select
                data-testid="blog-category-select"
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border bg-transparent text-sm focus:outline-none focus:border-petfolk-orange"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Excerpt</label>
              <textarea
                data-testid="blog-excerpt-input"
                value={form.excerpt}
                onChange={e => setForm({ ...form, excerpt: e.target.value })}
                placeholder="Short summary for previews & SEO (160 chars)"
                rows={2}
                className="w-full px-3 py-2 rounded-xl border bg-transparent text-sm focus:outline-none focus:border-petfolk-orange"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Tags (comma-separated)</label>
              <input
                data-testid="blog-tags-input"
                value={form.tags}
                onChange={e => setForm({ ...form, tags: e.target.value })}
                placeholder="grooming, dogs, summer"
                className="w-full px-3 py-2 rounded-xl border bg-transparent text-sm focus:outline-none focus:border-petfolk-orange"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Status</label>
              <select
                data-testid="blog-status-select"
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border bg-transparent text-sm focus:outline-none focus:border-petfolk-orange"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Cover Image */}
          <div className="mb-4">
            <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Cover Image</label>
            <div className="flex items-center gap-3">
              {form.coverImageUrl ? (
                <div className="relative">
                  <img src={form.coverImageUrl} alt="cover" className="h-20 w-32 object-cover rounded-lg border" style={{ borderColor: 'var(--border-color)' }} />
                  <button
                    onClick={() => setForm({ ...form, coverImageUrl: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    data-testid="blog-cover-remove"
                  ><X size={12} /></button>
                </div>
              ) : (
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed hover:bg-petfolk-orange/5"
                       style={{ borderColor: 'var(--border-color)' }}
                       data-testid="blog-cover-upload-label">
                  <ImageIcon size={16} />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Upload cover image</span>
                  <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" data-testid="blog-cover-upload" />
                </label>
              )}
            </div>
          </div>

          {/* Rich Text Editor */}
          <div className="mb-4" data-testid="blog-quill-wrapper">
            <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-secondary)' }}>Content *</label>
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={form.content}
                onChange={(html) => setForm(f => ({ ...f, content: html }))}
                modules={modules}
                formats={QUILL_FORMATS}
                placeholder="Write your story…"
                className="petfolk-quill"
              />
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Tip: use the toolbar's image button to insert images inline.</p>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <button onClick={cancelEdit} className="px-4 py-2 rounded-xl border text-sm" style={{ borderColor: 'var(--border-color)' }} data-testid="blog-cancel-bottom">
              Cancel
            </button>
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="px-4 py-2 rounded-xl border text-sm inline-flex items-center gap-2 disabled:opacity-50"
              style={{ borderColor: 'var(--border-color)' }}
              data-testid="blog-save-draft-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Draft
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="blog-publish-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />} {form.status === 'published' ? 'Update Published' : 'Publish'}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      {!editing && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              data-testid="blog-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search posts…"
              className="w-full pl-9 pr-3 py-2 rounded-xl border bg-transparent text-sm focus:outline-none focus:border-petfolk-orange"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
          <div className="flex gap-1">
            {['', 'published', 'draft', 'archived'].map(s => (
              <button
                key={s || 'all'}
                onClick={() => setStatusFilter(s)}
                data-testid={`blog-filter-${s || 'all'}`}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${statusFilter === s ? 'bg-petfolk-orange text-white' : 'border hover:bg-black/5 dark:hover:bg-white/5'}`}
                style={{ borderColor: statusFilter === s ? 'transparent' : 'var(--border-color)', color: statusFilter === s ? '#fff' : 'var(--text-secondary)' }}
              >
                {s ? STATUS_LABELS[s] : 'All'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* List */}
      {!editing && (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="blog-empty">
              No posts found. Click "New Post" to write your first story.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {filtered.map(blog => (
                <div key={blog.id} className="p-4 flex flex-wrap items-center gap-3 hover:bg-black/2 dark:hover:bg-white/2" data-testid={`blog-row-${blog.slug}`}>
                  {blog.coverImageUrl ? (
                    <img src={blog.coverImageUrl} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg shrink-0 flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
                      <FileText size={20} className="opacity-40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-outfit font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{blog.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[blog.status]}`}>{STATUS_LABELS[blog.status]}</span>
                      {blog.category && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-petfolk-orange/10 text-petfolk-orange">{blog.category}</span>
                      )}
                    </div>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      /{blog.slug} · {(blog.tags || []).slice(0, 3).join(', ') || 'no tags'} · {blog.viewCount || 0} views
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setPreviewing(blog)} className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" title="Preview" data-testid={`blog-preview-${blog.slug}`}>
                      <Eye size={16} />
                    </button>
                    {blog.status === 'published' && (
                      <a href={`/blog/${blog.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" title="Open public page">
                        <ExternalLink size={16} />
                      </a>
                    )}
                    <button onClick={() => togglePublish(blog)} className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" title={blog.status === 'published' ? 'Unpublish' : 'Publish'} data-testid={`blog-toggle-${blog.slug}`}>
                      {blog.status === 'published' ? <Archive size={16} /> : <Globe size={16} />}
                    </button>
                    <button onClick={() => startEdit(blog.id)} className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" title="Edit" data-testid={`blog-edit-${blog.slug}`}>
                      <Edit3 size={16} />
                    </button>
                    <button onClick={() => handleDelete(blog)} className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500" title="Delete" data-testid={`blog-delete-${blog.slug}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewing && (
        <PreviewModal blog={previewing} onClose={() => setPreviewing(null)} />
      )}

      {/* Quill custom theme tweaks */}
      <style>{`
        .petfolk-quill .ql-toolbar { background: var(--bg-primary); border: 0 !important; border-bottom: 1px solid var(--border-color) !important; }
        .petfolk-quill .ql-container { border: 0 !important; min-height: 320px; font-family: inherit; font-size: 15px; }
        .petfolk-quill .ql-editor { min-height: 320px; color: var(--text-primary); }
        .petfolk-quill .ql-editor img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
        .dark .petfolk-quill .ql-snow .ql-stroke { stroke: var(--text-secondary); }
        .dark .petfolk-quill .ql-snow .ql-fill { fill: var(--text-secondary); }
        .dark .petfolk-quill .ql-snow .ql-picker-label { color: var(--text-secondary); }
        .dark .petfolk-quill .ql-snow .ql-picker-options { background: var(--bg-card); border-color: var(--border-color); }
      `}</style>
    </div>
  );
}

function PreviewModal({ blog, onClose }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    apiFetch(`/api/admin/blog/${blog.id}`).then(setFull).catch(() => setFull(blog));
  }, [blog]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="blog-preview-modal">
      <div className="max-w-3xl w-full max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl" style={{ background: 'var(--bg-card)' }} onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b sticky top-0" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
          <div className="flex items-center justify-between">
            <h3 className="font-outfit font-bold text-lg truncate">{blog.title}</h3>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6">
          {full?.coverImageUrl && <img src={full.coverImageUrl} alt="" className="w-full rounded-xl mb-4" />}
          <article
            className="prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(full?.content || '<p>Loading…</p>') }}
          />
        </div>
      </div>
    </div>
  );
}
